import SendIcon from '@mui/icons-material/Send';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import { FC, KeyboardEvent, useState } from 'react';
import { FormattedMessage, MessageDescriptor, defineMessages } from 'react-intl';
import { Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';

// removed ramda cond/always usage
import { useQueryParam } from '../../hooks/useQueryParam';

const messages = defineMessages({
  idle: {
    id: 'badQreId.idle',
    defaultMessage: 'Tady se zatím nic neděje…',
  },
  loadingError: {
    id: 'badQreId.loadingError',
    defaultMessage: 'Nastala chyba při načítání dotazníku.',
  },
  notFound: {
    id: 'badQreId.notFound',
    defaultMessage: 'Vypadá to, že dotazník s ID <b>{qreId}</b> neexistuje.',
  },
  surveyNotFinalized: {
    id: 'badQreId.surveyNotFinalized',
    defaultMessage:
      'Dotazník <b>{qreId}</b> v QreBuilderu ještě není ve stavu FINAL. Po přepnutí do FINAL bude možné pokračovat v CatiGate.',
  },
  surveyNotCati: {
    id: 'badQreId.surveyNotCawi',
    defaultMessage:
      'Dotazník <b>{qreId}</b> nebyl v QreBuilderu vytvořen pro metodu CATI. Pracovat s ním v CatiGate proto není možné',
  },
  surveyNotNfield: {
    id: 'badQreId.surveyNotNfield',
    defaultMessage: 'Dotazník <b>{qreId}</b> není určen pro CATI Tool.',
  },
  externalSurveyNotReceived: {
    id: 'badQreId.externalSurveyNotReceived',
    defaultMessage:
      'Externí dotazník <b>{qreId}</b> zatím nedorazil z QreBuilderu. Převeďte ho v QreBuilderu do stavu Final.',
  },
  invalid: {
    id: 'badQreId.invalid',
    defaultMessage: 'Jejda, <b>{qreId}</b> není platné QRE ID.',
  },
});

export function ProblemTitle({
  qreIdValue,
  error,
}: {
  qreIdValue: string;
  error?: (Error & { status?: number; data?: { error?: string } }) | undefined;
}) {
  const rules: Array<{
    when: boolean;
    messageDescriptor: MessageDescriptor;
    values?: Record<string, React.ReactNode>;
  }> = [
    {
      when:
        (error as (Error & { status?: number }) | undefined)?.status === 404 ||
        (error as (Error & { error?: string }) | undefined)?.error === 'survey-not-found',
      messageDescriptor: messages.notFound,
      values: { qreId: qreIdValue },
    },
    {
      when: error?.data?.error === 'external-survey-not-received',
      messageDescriptor: messages.externalSurveyNotReceived,
      values: { qreId: qreIdValue },
    },
    {
      when: error?.data?.error === 'survey-not-nfield',
      messageDescriptor: messages.surveyNotNfield,
      values: { qreId: qreIdValue },
    },
    {
      when: error?.data?.error === 'survey-not-cati',
      messageDescriptor: messages.surveyNotCati,
      values: { qreId: qreIdValue },
    },
    {
      when: error?.data?.error === 'survey-not-finalized',
      messageDescriptor: messages.surveyNotFinalized,
      values: { qreId: qreIdValue },
    },
    {
      when: Boolean(error),
      messageDescriptor: messages.loadingError,
      values: { qreId: qreIdValue },
    },
    { when: !qreIdValue, messageDescriptor: messages.idle },
    { when: true, messageDescriptor: messages.invalid, values: { qreId: qreIdValue } },
  ];

  const matchedRule = rules.find((rule) => rule.when);

  const messageProps = { ...matchedRule?.messageDescriptor, values: matchedRule?.values };

  return (
    <Typography sx={{ textAlign: 'center' }}>
      <FormattedMessage {...messageProps} />
    </Typography>
  );
}

const BadQreIdPage: FC<{ qreIdValue: string; error?: Error }> = ({ qreIdValue, error }) => {
  const { value } = useQueryParam('qreId');
  const [qreId, setQreId] = useState<string>(value ?? '');
  const navigate = useNavigate();

  const handleSend = () => {
    if (qreId.trim()) {
      navigate(`/?qreId=${qreId.trim()}`);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleSend();
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 3,
        p: 2,
        height: '100vh',
      }}
    >
      <ProblemTitle error={error} qreIdValue={qreIdValue} />

      <Typography sx={{ textAlign: 'center' }}>
        <FormattedMessage
          id="badQreId.introOpen"
          defaultMessage="Brána CATI Gate se otevírá nejčastěji tlačítkem „CATI Gate“ v QreBuilderu."
        />
      </Typography>
      <Typography sx={{ textAlign: 'center' }}>
        <FormattedMessage
          id="badQreId.introLost"
          defaultMessage="Zabloudili jste? Žádný stres – vraťte se do QreBuilderu a klikněte na CATI Gate u svého dotazníku."
        />
      </Typography>
      <Typography sx={{ textAlign: 'center' }}>
        <FormattedMessage
          id="badQreId.introBrave"
          defaultMessage="Pro odvážné průzkumníky: Zadejte platné Survey qreId níže a stiskněte GO."
        />
      </Typography>

      <TextField
        label={<FormattedMessage id="badQreId.labelQreId" defaultMessage="QRE ID" />}
        variant="outlined"
        value={qreId}
        onChange={(e) => setQreId(e.target.value)}
        onKeyDown={handleKeyDown}
        sx={{ width: '100%', maxWidth: 400 }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={handleSend} edge="end" color="primary" disabled={!qreId.trim()}>
                <SendIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    </Box>
  );
};

export default BadQreIdPage;
